import { useCallback, useContext } from 'react';
import context from '.';

let id = 0;

/**
 * @typedef {(
 *   content: JSX.Element,
 *   error?: boolean,
 * ) => () => void} addToast
 * @typedef {(
 *   id: number,
 * ) => void} removeToast
 * 
 * @returns {[
 *   Array<{
 *     id: number;
 *     content: string;
 *     error: boolean;
 *   }>,
 *   {
 *     addToast: addToast,
 *     removeToast: removeToast,
 *   }
 * ]}
 */
const useToasts = () => {
  const { toasts, setToasts } = useContext(context);

  /**
   * Removes a toast
   * 
   * @type {removeToast}
   */
  const removeToast = useCallback((id) => {
    setToasts(toasts => toasts.filter(t => t.id !== id));
  }, []);

  /**
   * Adds a toast and returns a function that when called will remove
   * the toast
   * 
   * @type {addToast}
   */
  const addToast = useCallback((content, error = false) => {
    const _id = id++;
    setToasts(toasts => [
      ...toasts,
      {id, content, error},
    ]);
    return () => removeToast(_id);
  }, [removeToast]);

  return [
    toasts,
    {
      removeToast,
      addToast,
    },
  ]
}

export default useToasts;